import type { ReactElement } from 'react';
import { Button, cn } from '@/shared/ui';
import {
  customSelection,
  DASHBOARD_PERIOD_PRESET_LABELS,
  DASHBOARD_PERIOD_PRESETS,
  type DashboardPeriodSelection,
  periodLabel,
  presetSelection,
} from '../model/period';

interface PeriodTabsProps {
  readonly value: DashboardPeriodSelection;
  readonly onChange: (selection: DashboardPeriodSelection) => void;
  /** 프리셋 계산의 기준 시각 — 화면이 열린 시각. */
  readonly now: Date;
}

const DATE_INPUT_CLASS =
  'h-9 rounded-md border border-input bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * 기간 선택 — 오늘 / 최근 7일 / 최근 30일 / 기간 지정. 기간 지정을 고르면 날짜 칸 둘이 열리고, 고른 범위는 오른쪽에 보인다.
 * 기간 현황·인기 상품에만 걸린다(처리 대기·주의 필요 주문은 지금 쌓인 것).
 */
export const PeriodTabs = ({ value, onChange, now }: PeriodTabsProps): ReactElement => (
  <div className="flex flex-wrap items-center gap-3">
    <fieldset className="flex flex-wrap gap-1.5">
      <legend className="sr-only">기간</legend>
      {DASHBOARD_PERIOD_PRESETS.map((preset) => (
        <Button
          key={preset}
          type="button"
          size="sm"
          variant={value.preset === preset ? 'default' : 'outline'}
          aria-pressed={value.preset === preset}
          className={cn('h-9 rounded-full px-4', value.preset !== preset && 'border-foreground/15')}
          onClick={() => onChange(presetSelection(preset, now))}
        >
          {DASHBOARD_PERIOD_PRESET_LABELS[preset]}
        </Button>
      ))}
    </fieldset>
    {value.preset === 'custom' ? (
      <div className="flex items-center gap-2 text-sm">
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">시작</span>
          <input
            type="date"
            aria-label="시작일"
            className={DATE_INPUT_CLASS}
            value={value.from}
            max={value.to}
            onChange={(event) => {
              if (event.target.value) onChange(customSelection(event.target.value, value.to));
            }}
          />
        </label>
        <label className="flex items-center gap-1.5">
          <span className="text-muted-foreground">끝</span>
          <input
            type="date"
            aria-label="종료일"
            className={DATE_INPUT_CLASS}
            value={value.to}
            min={value.from}
            onChange={(event) => {
              if (event.target.value) onChange(customSelection(value.from, event.target.value));
            }}
          />
        </label>
      </div>
    ) : null}
    <p className="ml-auto text-sm tabular-nums text-muted-foreground" aria-live="polite">
      {periodLabel(value)}
    </p>
  </div>
);
